import axios from 'axios';
import moment from "moment/moment";

export default {
    namespaced: true,

    state: {
        payment: {},
        tennisIntegrityUnitStatus: false,
        loading: false,
        loadingError: null,
        associatedPlayersForAgent: null,
        annualFormValidation: null,
        isUserEnableToSignTheAnnualForm: false,
        loadingPlayerAnnualConsentFormError: null,
        annualOldDate: null,
        annualNewDate: null,
        healthOldDate:null,
        healthNewDate:null,
        tripwireStatuses: null,
    },

    getters: {
        payment: (state) => state.payment,
        tennisIntegrityUnitStatus: (state) => state.tennisIntegrityUnitStatus,
        loading: (state) => state.loading,
        loadingError: (state) => state.loadingError,
        associatedPlayersForAgent: (state) => state.associatedPlayersForAgent,
        annualFormValidation: (state) => state.annualFormValidation,
        isUserEnableToSignTheAnnualForm: (state) => state.isUserEnableToSignTheAnnualForm,
        hasThePlayerAnnualConsentFormBeenSigned: (state) => {
            return state.annualFormValidation && state.annualFormValidation.signatureDateForm;
        },
        loadingPlayerAnnualConsentFormError: (state) => state.loadingPlayerAnnualConsentFormError,
        annualOldDate: (state) => state.annualOldDate,
        annualNewDate: (state) => state.annualNewDate,
        healthOldDate: (state) => state.healthOldDate,
        healthNewDate: (state) => state.healthNewDate,
        tripwireStatuses: (state) => state.tripwireStatuses,
    },

    mutations: {
        setFormsDates(state, annualOldDate, annualNewDate, healthOldDate, healthNewDate) {
            state.annualOldDate = annualOldDate;
            state.annualNewDate = annualNewDate;
            state.healthOldDate = healthOldDate;
            state.healthNewDate = healthNewDate;
        },

        setPayment(state, data) {
            state.payment = data;
        },

        setTennisIntegrityUnitStatus(state, data) {
            state.tennisIntegrityUnitStatus = data;
        },

        setPlayersAssociatedToAgent(state, data) {
            state.associatedPlayersForAgent = data
        },

        setAnnualFormValidation(state, data) {
            state.annualFormValidation = data;
        },

        startLoading(state) {
            state.loading = true;
        },

        stopLoading(state) {
            state.loading = false;
        },

        setLoadingError(state, error) {
            state.loadingError = error;
        },

        clearLoadingError(state) {
            state.loadingError = null;
        },

        setIsUserEnableToSignTheAnnualForm(state, data) {
            state.isUserEnableToSignTheAnnualForm = data;
        },

        setLoadingPlayerAnnualConsentFormError(state, error) {
            state.loadingPlayerAnnualConsentFormError = error;
        },

        setTripwireStatuses(state, data) {
            state.tripwireStatuses = data
        },
    },

    actions: {
        async retrieveFormsStartDates(context, options = {}) {
            try {
                const result = await axios.get(`${process.env.VUE_APP_API_URL}/PzPaymentDate/`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                let annualOldDate = result.data.find(item => item && item.name === 'Annual-Forms-Old-Start-Date').date
                let annualNewDate = result.data.find(item => item && item.name === 'Annual-Forms-New-Start-Date').date
                let healthOldDate = result.data.find(item => item && item.name === 'Health-Forms-New-Start-Date').date
                let healthNewDate = result.data.find(item => item && item.name === 'Health-Forms-Old-Start-Date').date

                context.commit('setFormsDates', annualOldDate, annualNewDate, healthOldDate, healthNewDate);
                return Promise.resolve([annualOldDate, annualNewDate]);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingError', error);
                return Promise.reject(error);
            }
        },
        async retrievePaymentInformation(context, options = {}) {
            try {
                const result = await axios.get(`${process.env.VUE_APP_API_URL}/access/PaymentInformation/${options.playerIdentifier}`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                context.commit('setPayment', result.data);
                return Promise.resolve(result.data);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingError', error);
                return Promise.reject(error);
            }
        },
        async retrieveTennisIntegrityUnitStatus(context, options = {}) {
            try {
                if(options.playerIdentifier.startsWith("C")) {
                    context.commit('stopLoading');
                    return Promise.resolve({});
                }

                const result = await axios.get(`${process.env.VUE_APP_API_URL}/access/TennisIntegrityUnitInformationForPlayer/${options.playerIdentifier}`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                context.commit('setTennisIntegrityUnitStatus', result.data);
                return Promise.resolve(result.data);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingError', error);
                return Promise.reject(error);
            }
        },
        async retrievePlayersAssociatedToAgent(context, options = {}) {
            try {
                const result = await axios.get(`${process.env.VUE_APP_API_URL}/Player/ByAgentEmail/${options.userEmail}`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                context.commit('setPlayersAssociatedToAgent', result.data);
                return Promise.resolve(result.data);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingError', error);
                return Promise.reject(error);
            }
        },
        async retrieveAnnualFormValidation(context, options = {}) {
            try {
                if (options.playerIdentifier.startsWith("C")) {
                    context.commit('stopLoading');
                    return Promise.resolve(null);
                }

                const result = await axios.get(`${process.env.VUE_APP_API_URL}/playerconsentform/${options.playerIdentifier}/${options.playerEmail}/annual-form`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                context.commit('setAnnualFormValidation', result.data);
                return Promise.resolve(result.data);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingPlayerAnnualConsentFormError', error.response.data.name);
                // Handle only the 500 status code here
                context.commit('setLoadingError', error);
            }
        },
        async retrieveTripwireValidations(context, options = {}) {
            try {
                const result = await axios.get(`${process.env.VUE_APP_API_URL}/access/GetTripwireStatuses/${options.playerNumber}`);
                context.commit('stopLoading');
                context.commit('clearLoadingError');
                context.commit('setTripwireStatuses', result.data);
                return Promise.resolve(result.data);
            } catch (error) {
                context.commit('stopLoading');
                context.commit('setLoadingError', error);
                return Promise.reject(error);
            }
        },
    },
};
